import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ProveedorCombobox } from "@/components/ProveedorCombobox";
import { ProductoCombobox } from "@/components/ProductoCombobox";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Trash2, Edit2, Check, X, Download, Eye, Undo2 } from "lucide-react";

const ESTADOS = {
  borrador: { label: "Borrador", cls: "bg-[#e6ebe8] text-[#5c7368]" },
  aprobado: { label: "Aprobado", cls: "bg-[#205c45] text-white" },
  anulado: { label: "Anulado", cls: "bg-[#fdecea] text-[#b53a25] border border-[#d99584]" },
};

export default function DevolucionesProveedor() {
  const { user } = useAuth();
  const esAdmin = (user?.role || "") === "admin";
  const [items, setItems] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [productos, setProductos] = useState([]);
  const [motivos, setMotivos] = useState([]);
  const [facturas, setFacturas] = useState([]);
  const [filterEstado, setFilterEstado] = useState("");
  const [busqueda, setBusqueda] = useState("");           // ITER213
  const [facturasLote, setFacturasLote] = useState([]);   // ITER213: facturas del lote elegido
  const [facturaManual, setFacturaManual] = useState(false);
  const [proveedorSugerido, setProveedorSugerido] = useState(null); // ITER217
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState(empty());

  function empty() {
    return {
      proveedor_id: "", factura_compra_id: "", factura_compra_numero: "",
      ciudad: "Guayaquil", fecha: new Date().toISOString().slice(0, 10),
      motivo_codigo: "caducidad", motivo_detalle: "",
      representante_tecnico_nombre: "", representante_tecnico_cargo: "REPRESENTANTE TÉCNICO",
      recibe_nombre: "", recibe_cargo: "REPRESENTANTE DE VENTA",
      observaciones: "",
      lineas: [emptyLinea()],
    };
  }
  function emptyLinea() {
    return { producto_comercial_id: "", producto_nombre: "", presentacion: "",
             lote_id: "", numero_lote: "", cantidad: 1, unidad: "unidad", unidades_por_caja: null, fecha_vencimiento: "",
             _lotes: [] };
  }

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (filterEstado) params.set("estado", filterEstado);
      if (busqueda.trim()) params.set("q_text", busqueda.trim());
      const [{ data }, p, pc, m, fc] = await Promise.all([
        api.get(`/devoluciones-proveedor?${params}`),
        api.get("/masters/proveedores"),
        api.get("/masters/productos-comerciales"),
        api.get("/devoluciones-proveedor/motivos"),
        api.get("/purchases/facturas").catch(() => ({ data: [] })),
      ]);
      setItems(data); setProveedores(p.data); setProductos(pc.data); setMotivos(m.data); setFacturas(fc.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => {
    const t = setTimeout(() => { load(); }, busqueda ? 400 : 0);
    return () => clearTimeout(t);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [filterEstado, busqueda]);

  const addLinea = () => setForm(f => ({ ...f, lineas: [...f.lineas, emptyLinea()] }));
  const removeLinea = (i) => setForm(f => ({ ...f, lineas: f.lineas.filter((_, x) => x !== i) }));

  const onProductoChange = async (i, prodId) => {
    setForm(f => {
      const n = [...f.lineas];
      const p = productos.find(x => x.id === prodId);
      n[i] = {
        ...n[i],
        producto_comercial_id: prodId,
        producto_nombre: p?.nombre_comercial || "",
        presentacion: p?.presentacion_nombre || p?.presentacion || "",
        unidades_por_caja: p?.unidades_por_caja || null,
        lote_id: "", numero_lote: "", fecha_vencimiento: "",
        _lotes: [],
      };
      return { ...f, lineas: n };
    });
    if (prodId) {
      try {
        const { data } = await api.get(`/devoluciones-proveedor/_/lotes-disponibles/${prodId}`);
        setForm(f => {
          const n = [...f.lineas];
          n[i] = { ...n[i], _lotes: data };
          return { ...f, lineas: n };
        });
      } catch (e) { /* ignore */ }
      // ITER217: autocompletar el proveedor a partir del producto elegido
      try {
        const { data } = await api.get(`/devoluciones-proveedor/_/proveedor-sugerido?producto_comercial_id=${prodId}`);
        if (data?.proveedor_id) {
          setForm(f => (f.proveedor_id ? f : { ...f, proveedor_id: data.proveedor_id }));
          setProveedorSugerido(data);
        }
      } catch (e) { /* ignore */ }
    }
  };
  const onLoteChange = async (i, loteId) => {
    let lote = null, prodId = "";
    setForm(f => {
      const n = [...f.lineas];
      const lo = (n[i]._lotes || []).find(x => x.id === loteId);
      lote = lo?.numero_lote || "";
      prodId = n[i].producto_comercial_id;
      n[i] = {
        ...n[i],
        lote_id: loteId,
        numero_lote: lo?.numero_lote || "",
        fecha_vencimiento: lo?.fecha_vencimiento || "",
      };
      return { ...f, lineas: n };
    });
    // ITER213: cargar las facturas de compra donde aparece ese lote
    if (prodId) {
      try {
        const qs = new URLSearchParams({ producto_comercial_id: prodId });
        if (lote) qs.set("numero_lote", lote);
        if (form.proveedor_id) qs.set("proveedor_id", form.proveedor_id);
        const { data } = await api.get(`/devoluciones-proveedor/_/facturas-por-lote?${qs}`);
        setFacturasLote(data || []);
        if (!data?.length) setFacturaManual(true);
      } catch (e) { setFacturasLote([]); }
    }
  };

  const updateLinea = (i, field, val) => setForm(f => {
    const n = [...f.lineas]; n[i] = { ...n[i], [field]: val }; return { ...f, lineas: n };
  });

  const totalCantidad = useMemo(() => form.lineas.reduce((s, l) => s + (parseInt(l.cantidad) || 0), 0), [form.lineas]);
  const totalUnidades = useMemo(() => form.lineas.reduce((s, l) => {
    const c = parseInt(l.cantidad) || 0;
    const upc = parseInt(l.unidades_por_caja) || 0;
    return s + (l.unidad === "caja" && upc > 1 ? c * upc : c);
  }, 0), [form.lineas]);

  const save = async () => {
    if (!form.proveedor_id) return toast.error("Seleccione proveedor");
    const lineasClean = form.lineas
      .filter(l => l.producto_comercial_id && (parseInt(l.cantidad) || 0) > 0)
      .map(({ _lotes, ...l }) => ({
        ...l,
        cantidad: parseInt(l.cantidad),
        unidad: l.unidad || "unidad",
        unidades_por_caja: l.unidad === "caja" ? (parseInt(l.unidades_por_caja) || null) : null,
      }));
    if (lineasClean.length === 0) return toast.error("Agregue al menos un producto con cantidad > 0");
    const payload = {
      proveedor_id: form.proveedor_id,
      factura_compra_id: form.factura_compra_id || null,
      factura_compra_numero: form.factura_compra_numero || null,
      ciudad: form.ciudad, fecha: form.fecha,
      motivo_codigo: form.motivo_codigo, motivo_detalle: form.motivo_detalle || null,
      representante_tecnico_nombre: form.representante_tecnico_nombre || null,
      representante_tecnico_cargo: form.representante_tecnico_cargo,
      recibe_nombre: form.recibe_nombre || null,
      recibe_cargo: form.recibe_cargo,
      observaciones: form.observaciones || null,
      lineas: lineasClean,
    };
    try {
      if (editingId) {
        await api.patch(`/devoluciones-proveedor/${editingId}`, payload);
        toast.success("Devolución actualizada");
      } else {
        await api.post("/devoluciones-proveedor", payload);
        toast.success("Devolución creada (borrador)");
      }
      setDialogOpen(false); setEditingId(null); setForm(empty()); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const editar = async (it) => {
    setEditingId(it.id);
    // ITER213: si la devolución guardó un número de factura sin id, se edita a mano
    setFacturasLote([]);
    setFacturaManual(!!it.factura_compra_numero && !it.factura_compra_id);
    // Re-cargar lotes para cada línea
    const lineas = await Promise.all((it.lineas || []).map(async (l) => {
      let _lotes = [];
      if (l.producto_comercial_id) {
        try {
          const { data } = await api.get(`/devoluciones-proveedor/_/lotes-disponibles/${l.producto_comercial_id}`);
          _lotes = data;
        } catch (_) { _lotes = []; }
      }
      return { ...l, _lotes };
    }));
    setForm({
      proveedor_id: it.proveedor_id, factura_compra_id: it.factura_compra_id || "",
      factura_compra_numero: it.factura_compra_numero || "",
      ciudad: it.ciudad, fecha: it.fecha,
      motivo_codigo: it.motivo_codigo, motivo_detalle: it.motivo_detalle || "",
      representante_tecnico_nombre: it.representante_tecnico_nombre || "",
      representante_tecnico_cargo: it.representante_tecnico_cargo || "REPRESENTANTE TÉCNICO",
      recibe_nombre: it.recibe_nombre || "", recibe_cargo: it.recibe_cargo || "REPRESENTANTE DE VENTA",
      observaciones: it.observaciones || "",
      lineas: lineas.length ? lineas : [emptyLinea()],
    });
    setDialogOpen(true);
  };

  const aprobar = async (it) => {
    if (!window.confirm(`¿Aprobar devolución ${it.numero}?\n\nNO se descuenta inventario: el stock se ajusta cuando el proveedor emita su nota de crédito.`)) return;
    try { await api.post(`/devoluciones-proveedor/${it.id}/aprobar`); toast.success("Aprobada · el inventario se ajustará con la NC del proveedor", { duration: 7000 }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const anular = async (it) => {
    if (!window.confirm(`¿Anular devolución ${it.numero}?`)) return;
    try { await api.post(`/devoluciones-proveedor/${it.id}/anular`); toast.success("Devolución anulada"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const eliminar = async (it) => {
    if (!window.confirm(`¿Eliminar devolución ${it.numero}?`)) return;
    try { await api.delete(`/devoluciones-proveedor/${it.id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const verPdf = async (it) => {
    try {
      const r = await api.get(`/devoluciones-proveedor/${it.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Facturas filtradas por proveedor seleccionado
  const facturasProv = useMemo(
    () => facturas.filter(f => f.proveedor_id === form.proveedor_id),
    [facturas, form.proveedor_id]
  );

  return (
    <div className="space-y-6" data-testid="devoluciones-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Bodega</div>
        <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Devoluciones a Proveedor
        </h1>
        <p className="text-sm text-[#4a5c53] mt-1">Documentos formales de entrega de medicamentos al laboratorio (caducidad, defecto, recall, etc.). Al aprobar genera el PDF imprimible; el inventario se descuenta después, con la nota de crédito del proveedor.</p>
      </div>

      <div className="rounded-md border border-[#d1dbd6] bg-white p-3 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">Estado</Label>
          <Select value={filterEstado || "all"} onValueChange={(v) => setFilterEstado(v === "all" ? "" : v)}>
            <SelectTrigger className="w-44" data-testid="dev-estado-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {Object.entries(ESTADOS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[280px] flex-1">
          <Label className="text-xs">Buscar</Label>
          <Input value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Proveedor, producto, lote o N° documento…" data-testid="dev-search" />
        </div>
        <div className="ml-auto">
          <Button onClick={() => { setEditingId(null); setForm(empty()); setFacturasLote([]); setFacturaManual(false); setDialogOpen(true); }} className="bg-[#205c45]" data-testid="create-dev-btn">
            <Plus size={16} className="mr-2" /> Nueva devolución
          </Button>
        </div>
      </div>

      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Nº</th><th>Fecha</th><th>Laboratorio</th><th>Factura ref.</th><th>Motivo</th><th className="text-right">Líneas</th><th>Estado</th><th className="w-44 text-right">Acciones</th></tr></thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id} data-testid={`dev-row-${it.id}`}>
                <td className="font-mono text-xs">{it.numero}</td>
                <td className="text-xs">{it.fecha}</td>
                <td className="font-medium">{it.proveedor_nombre}</td>
                <td className="font-mono text-xs">{it.factura_compra_numero || "—"}</td>
                <td className="text-xs">{it.motivo_label}</td>
                <td className="text-right tabular-nums">{(it.lineas || []).reduce((s, l) => s + (l.cantidad || 0), 0)}</td>
                <td><Badge className={ESTADOS[it.estado]?.cls}>{ESTADOS[it.estado]?.label}</Badge></td>
                <td>
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" title="Ver PDF" onClick={() => verPdf(it)} data-testid={`dev-pdf-${it.id}`}><Download size={14} /></Button>
                    <Button size="icon" variant="ghost" title="Detalle" onClick={() => setViewing(it)}><Eye size={14} /></Button>
                    {it.estado === "borrador" && (
                      <>
                        <Button size="icon" variant="ghost" onClick={() => editar(it)}><Edit2 size={14} /></Button>
                        <Button size="sm" variant="outline" onClick={() => aprobar(it)} className="text-[#205c45]" data-testid={`dev-aprobar-${it.id}`}>
                          <Check size={14} className="mr-1" />Aprobar
                        </Button>
                        {esAdmin && (
                          <Button size="icon" variant="ghost" title="Eliminar (solo administrador)" onClick={() => eliminar(it)} data-testid={`dev-eliminar-${it.id}`}>
                            <Trash2 size={14} className="text-[#b53a25]" />
                          </Button>
                        )}
                      </>
                    )}
                    {it.estado === "aprobado" && (
                      <Button size="sm" variant="outline" onClick={() => anular(it)} className="text-[#b53a25]"><Undo2 size={14} className="mr-1" />Anular</Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-[#8ba397]">Sin devoluciones registradas</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Crear/editar */}
      <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) setEditingId(null); }}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar devolución" : "Nueva devolución a proveedor"}</DialogTitle>
            <DialogDescription>Documento formal de entrega de medicamentos al laboratorio. No mueve inventario: el stock se ajusta con la nota de crédito del proveedor.</DialogDescription>
          </DialogHeader>
          {/* ITER217: primero el producto (autocompleta el proveedor), luego proveedor/ciudad,
              fecha y factura, motivo/observaciones y al final las firmas */}
          <div className="border border-[#d1dbd6] rounded-md">
            <div className="flex items-center justify-between px-3 py-2 bg-[#f7f9f8] border-b">
              <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">Productos a devolver</div>
              <Button size="sm" variant="outline" onClick={addLinea}><Plus size={14} className="mr-1" />Agregar producto</Button>
            </div>
            <table className="erp-table">
              <thead><tr><th>Producto</th><th>Lote / Vencimiento</th><th className="w-24">Cantidad</th><th className="w-32">Unidad</th><th className="w-10"></th></tr></thead>
              <tbody>
                {form.lineas.map((l, i) => (
                  <tr key={i}>
                    <td>
                      <ProductoCombobox
                        value={l.producto_comercial_id}
                        onChange={(v) => onProductoChange(i, v)}
                        productos={productos}
                        testId={`dev-prod-${i}`}
                      />
                    </td>
                    <td>
                      <Select value={l.lote_id || "none"} onValueChange={(v) => onLoteChange(i, v === "none" ? "" : v)}>
                        <SelectTrigger className="h-9" data-testid={`dev-lote-${i}`}><SelectValue placeholder={l._lotes?.length ? "Elegir lote..." : "Sin lotes activos"} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— Sin lote (devolución sin stock) —</SelectItem>
                          {(l._lotes || []).map(lo => (
                            <SelectItem key={lo.id} value={lo.id}>{lo.numero_lote} · vence {lo.fecha_vencimiento} · stock {lo.cantidad_disponible}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td><Input type="number" min="1" value={l.cantidad} onChange={(e) => updateLinea(i, "cantidad", parseInt(e.target.value) || 0)} className="h-9 text-right tabular-nums" data-testid={`dev-cantidad-${i}`} /></td>
                    <td>
                      <Select value={l.unidad || "unidad"} onValueChange={(v) => updateLinea(i, "unidad", v)}>
                        <SelectTrigger className="h-9" data-testid={`dev-unidad-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unidad">Unidades</SelectItem>
                          <SelectItem value="caja">Cajas</SelectItem>
                        </SelectContent>
                      </Select>
                      {l.unidad === "caja" && (
                        <Input type="number" min="1" value={l.unidades_por_caja || ""}
                          onChange={(e) => updateLinea(i, "unidades_por_caja", parseInt(e.target.value) || null)}
                          placeholder="u. x caja" className="h-7 mt-1 text-xs text-right tabular-nums"
                          data-testid={`dev-upc-${i}`} />
                      )}
                    </td>
                    <td><Button size="icon" variant="ghost" onClick={() => removeLinea(i)}><Trash2 size={14} className="text-[#b53a25]" /></Button></td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-[#f7f9f8] border-t">
                <tr><td colSpan={2} className="text-right font-semibold uppercase text-xs tracking-wider text-[#5c7368] px-3 py-2">Total a devolver</td><td className="text-right font-bold tabular-nums px-3 py-2">{totalCantidad}</td><td colSpan={2} className="text-xs text-[#5c7368] px-3">= {totalUnidades} unidades físicas</td></tr>
              </tfoot>
            </table>
          </div>

          <div className="grid grid-cols-3 gap-3 mt-4">
            <div className="col-span-2">
              <Label>Laboratorio / Proveedor</Label>
              <ProveedorCombobox value={form.proveedor_id} onChange={(v) => setForm(f => ({ ...f, proveedor_id: v, factura_compra_id: "", factura_compra_numero: "" }))} proveedores={proveedores} testId="dev-prov" />
            </div>
            {proveedorSugerido?.proveedor_id && (
              <div className="col-span-3 text-[11px] text-[#205c45] bg-[#e9f2ee] border border-[#b2d0c2] rounded px-2 py-1" data-testid="dev-prov-sugerido">
                Proveedor tomado {proveedorSugerido.origen === "ultima_compra"
                  ? `de la última compra del producto${proveedorSugerido.factura_numero ? ` (factura ${proveedorSugerido.factura_numero})` : ""}`
                  : "del laboratorio del producto"}. Puede cambiarlo si corresponde.
              </div>
            )}
            <div><Label>Ciudad</Label><Input value={form.ciudad} onChange={(e) => setForm({ ...form, ciudad: e.target.value })} /></div>
          </div>

          <div className="grid grid-cols-3 gap-3 mt-4">
            <div><Label>Fecha</Label><Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} data-testid="dev-fecha" /></div>
            <div className="col-span-2">
              <Label>Factura de compra del lote</Label>
              {facturaManual ? (
                <div className="flex gap-2">
                  <Input value={form.factura_compra_numero}
                    onChange={(e) => setForm(f => ({ ...f, factura_compra_numero: e.target.value, factura_compra_id: "" }))}
                    placeholder="Escriba el N° de factura (ej. 001-002-000123456)"
                    data-testid="dev-factura-manual" />
                  <Button variant="outline" size="sm" onClick={() => setFacturaManual(false)} data-testid="dev-factura-lista">
                    Elegir de la lista
                  </Button>
                </div>
              ) : (
                <Select value={form.factura_compra_id || "none"} onValueChange={(v) => {
                  if (v === "manual") { setFacturaManual(true); setForm(f => ({ ...f, factura_compra_id: "" })); return; }
                  if (v === "none") { setForm(f => ({ ...f, factura_compra_id: "", factura_compra_numero: "" })); return; }
                  const opciones = facturasLote.length ? facturasLote : facturasProv.map(x => ({ factura_id: x.id, numero_factura: x.numero_factura }));
                  const fc = opciones.find(x => x.factura_id === v);
                  setForm(f => ({ ...f, factura_compra_id: v, factura_compra_numero: fc?.numero_factura || "" }));
                }}>
                  <SelectTrigger data-testid="dev-factura-select">
                    <SelectValue placeholder={facturasLote.length ? "Elegir factura del lote…" : "Elija primero producto y lote"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Sin factura —</SelectItem>
                    <SelectItem value="manual">✏️ Escribir el número manualmente</SelectItem>
                    {(facturasLote.length ? facturasLote : facturasProv.map(x => ({
                      factura_id: x.id, numero_factura: x.numero_factura, fecha: x.fecha_factura || x.fecha, match: "producto",
                    }))).map(f => (
                      <SelectItem key={f.factura_id} value={f.factura_id}>
                        {f.numero_factura} {f.fecha ? `· ${f.fecha}` : ""}{f.match === "producto" ? " · (sin vínculo con el lote)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <p className="text-[11px] text-[#5c7368] mt-1">
                Aquí aparecen las facturas donde ingresó el lote seleccionado arriba.
                Si el lote no tiene factura registrada, use “Escribir el número manualmente”.
              </p>
            </div>
            <div>
              <Label>Motivo</Label>
              <Select value={form.motivo_codigo} onValueChange={(v) => setForm({ ...form, motivo_codigo: v })}>
                <SelectTrigger data-testid="dev-motivo"><SelectValue /></SelectTrigger>
                <SelectContent>{motivos.map(m => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.motivo_codigo === "otro" && <div className="col-span-3"><Label>Detalle del motivo</Label><Input value={form.motivo_detalle} onChange={(e) => setForm({ ...form, motivo_detalle: e.target.value })} /></div>}
            <div className="col-span-3"><Label>Observaciones</Label><Textarea rows={2} value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} /></div>
          </div>

          <div className="grid grid-cols-3 gap-3 mt-4 pt-3 border-t border-[#d1dbd6]">
            <div className="col-span-3 text-xs uppercase tracking-wider font-semibold text-[#5c7368]">Firmas</div>
            <div><Label>Representante técnico (entrega)</Label><Input value={form.representante_tecnico_nombre} onChange={(e) => setForm({ ...form, representante_tecnico_nombre: e.target.value })} placeholder="Q.F. ESTHER JARAMILLO CORDOVA" /></div>
            <div><Label>Cargo entrega</Label><Input value={form.representante_tecnico_cargo} onChange={(e) => setForm({ ...form, representante_tecnico_cargo: e.target.value })} /></div>
            <div></div>
            <div><Label>Recibe (vendedor laboratorio)</Label><Input value={form.recibe_nombre} onChange={(e) => setForm({ ...form, recibe_nombre: e.target.value })} placeholder="SR. RICARDO VALVERDE" /></div>
            <div><Label>Cargo recibe</Label><Input value={form.recibe_cargo} onChange={(e) => setForm({ ...form, recibe_cargo: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-[#205c45]" data-testid="save-dev-btn">Guardar borrador</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detalle */}
      <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Devolución {viewing?.numero}</DialogTitle>
            <DialogDescription>Detalle del documento y productos devueltos al laboratorio</DialogDescription>
          </DialogHeader>
          {viewing && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Laboratorio</Label><div className="font-medium">{viewing.proveedor_nombre}</div></div>
                <div><Label className="text-xs">Estado</Label><div><Badge className={ESTADOS[viewing.estado]?.cls}>{ESTADOS[viewing.estado]?.label}</Badge></div></div>
                <div><Label className="text-xs">Fecha</Label><div>{viewing.fecha}</div></div>
                <div><Label className="text-xs">Factura ref.</Label><div className="font-mono text-xs">{viewing.factura_compra_numero || "—"}</div></div>
                <div className="col-span-2"><Label className="text-xs">Motivo</Label><div>{viewing.motivo_label}{viewing.motivo_detalle && ` · ${viewing.motivo_detalle}`}</div></div>
              </div>
              <table className="erp-table">
                <thead><tr><th>Producto</th><th>Lote</th><th>Presentación</th><th className="text-right">Cant.</th><th>Vence</th></tr></thead>
                <tbody>
                  {viewing.lineas?.map((l, i) => (
                    <tr key={i}>
                      <td>{l.producto_nombre}</td>
                      <td className="font-mono text-xs">{l.numero_lote || "—"}</td>
                      <td>{l.presentacion}</td>
                      <td className="text-right tabular-nums">
                        {l.cantidad} <span className="text-[10px] text-[#5c7368]">{(l.unidad || "unidad") === "caja" ? `caja(s)${l.unidades_por_caja ? ` x${l.unidades_por_caja}` : ""}` : "u."}</span>
                      </td>
                      <td className="text-xs">{l.fecha_vencimiento || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
